import { useCallback, useState } from 'react';
import {
  SoChiTietCongNoTheoKhachHangItem,
  SoChiTietCongNoTheoKhachHangResponse,
  SoChiTietCongNoTheoKhachHangSearchFormValues,
  UseSoChiTietCongNoTheoKhachHangReturn
} from '@/types/schemas';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

/**
 * Custom hook for managing SoChiTietCongNoTheoKhachHang (Customer Debt Detail Report) data
 *
 * This hook provides functionality to fetch customer debt detail data
 * with mock support for testing and development purposes.
 */
export function useSoChiTietCongNoTheoKhachHang(
  entitySlug: string,
  searchParams: SoChiTietCongNoTheoKhachHangSearchFormValues
): UseSoChiTietCongNoTheoKhachHangReturn {
  const [data, setData] = useState<SoChiTietCongNoTheoKhachHangItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async (searchParams: SoChiTietCongNoTheoKhachHangSearchFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<SoChiTietCongNoTheoKhachHangResponse>(
        `/entities/${entitySlug}/erp/${QUERY_KEYS.SO_CHI_TIET_CONG_NO_THEO_KHACH_HANG}/`,
        searchParams
      );

      setData(response.data.results || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred while fetching data';
      setError(new Error(errorMessage));
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await fetchData(searchParams);
  }, [fetchData, searchParams]);

  return {
    data,
    isLoading,
    error,
    fetchData,
    refreshData
  };
}

export const fetchAllSoChiTietCongNoTheoKhachHang = async (
  entitySlug: string,
  body: Record<string, any> = {},
  pageSize = 200
): Promise<SoChiTietCongNoTheoKhachHangItem[]> => {
  let page = 1;
  let all: SoChiTietCongNoTheoKhachHangItem[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.post<SoChiTietCongNoTheoKhachHangResponse>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.SO_CHI_TIET_CONG_NO_THEO_KHACH_HANG}/`,
      body,
      {
        params: {
          page,
          page_size: pageSize
        }
      }
    );

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    // Dừng nếu không còn kết quả hoặc đã lấy đủ dữ liệu
    if (!pageResults.length || (totalCount > 0 && all.length >= totalCount)) {
      break;
    }

    page += 1;
  }

  return all;
};
